import { faTag } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as React from 'react';
import { cn } from '../../utils/cn';

/**
 * Unified promo indicator across the storefront. Used wherever an
 * applied promo / sale needs to be surfaced inline with a row,
 * card, or detail panel:
 *
 *   • Catalog card        → variant="subtle"
 *   • Product detail page → variant="accent"
 *   • Cart line           → variant="inline"
 *   • Order detail line   → variant="inline"
 *   • Favorites           → variant="subtle"
 *
 * Why one component instead of five ad-hoc spans? Three reasons:
 *
 *   1. Visual consistency. The cart used a green pill, the catalog
 *      card had a different green pill, the detail page had a
 *      banner. Same data, three styles. Customers had to relearn
 *      the iconography on every screen.
 *   2. Information consistency. The chip should always name the
 *      promo that produced the savings — not the headline-ranker's
 *      pick. Centralising the chip lets us also centralise the
 *      data source (engine-applied promo) per surface.
 *   3. Affordance. A small sale-tag icon does heavy lifting at
 *      glance distance, especially in dense list rows. Without it
 *      the green pill reads as "status" rather than "discount".
 *
 * Translation: callers pass already-translated strings (matches the
 * existing `Badge` convention in this file tree). Promo names are
 * dynamic data so the consumer must `t()` them at the call site.
 */
export type PromoChipVariant = 'inline' | 'subtle' | 'accent';

export interface PromoChipProps {
	/**
	 * The applied promo's name, ALREADY TRANSLATED by the caller.
	 * The chip names the promo that the engine actually applied —
	 * see PriceContractsService / PromoPricingService for where
	 * that comes from.
	 */
	name: string;
	/**
	 * Optional discount badge text rendered as a secondary pill on
	 * the right (e.g. "10% OFF", "$5 OFF", "FREE", "B2G1"). Already
	 * translated by the caller. Skip for variants where space is
	 * tight ("inline") — the savings number is read from the
	 * strikethrough adjacent to the chip.
	 */
	badge?: string | null;
	variant?: PromoChipVariant;
	className?: string;
	/**
	 * Optional click handler. Used on the detail page to scroll to
	 * the promo terms section, or on a list to drill into the promo
	 * card. Inline variant typically isn't clickable.
	 */
	onClick?: () => void;
}

// Variant tokens — built on top of the same emerald palette the
// existing cart chip uses, so this is a drop-in visual replacement.
// Dark-mode pairs picked to match Badge.tsx's success variant.
const VARIANT_CLASSES: Record<PromoChipVariant, string> = {
	inline:
		'text-[10px] font-semibold uppercase tracking-wide bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200 px-2 py-0.5 gap-1',
	subtle:
		'text-xs font-medium bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-200 ring-1 ring-emerald-200 dark:ring-emerald-700/40 px-2.5 py-1 gap-1.5',
	accent:
		'text-sm font-semibold bg-emerald-600 text-white dark:bg-emerald-500 dark:text-white px-3 py-1.5 gap-2 shadow-sm',
};

const ICON_SIZE_CLASSES: Record<PromoChipVariant, string> = {
	inline: 'text-[9px]',
	subtle: 'text-[11px]',
	accent: 'text-sm',
};

const BADGE_CLASSES: Record<PromoChipVariant, string> = {
	// inline doesn't carry a sub-badge — the strikethrough adjacent
	// to the row already names the savings amount, and a second
	// pill inside a pill on a 10px chip is unreadable.
	inline: '',
	subtle:
		'ml-1 -mr-0.5 text-[10px] font-bold uppercase tracking-wider bg-emerald-200 text-emerald-900 dark:bg-emerald-700/60 dark:text-emerald-50 rounded px-1 py-px',
	accent:
		'ml-1 text-xs font-bold uppercase tracking-wider bg-white/25 dark:bg-black/20 text-white rounded px-1.5 py-0.5',
};

export function PromoChip({
	name,
	badge,
	variant = 'inline',
	className,
	onClick,
}: PromoChipProps): React.ReactElement {
	const interactive = onClick != null;
	const Element = interactive ? 'button' : 'span';
	// Inline variant deliberately ignores `badge` — see BADGE_CLASSES.
	const showBadge = variant !== 'inline' && badge != null && badge !== '';

	return (
		<Element
			type={interactive ? 'button' : undefined}
			onClick={onClick}
			title={name}
			className={cn(
				'inline-flex items-center rounded-full',
				VARIANT_CLASSES[variant],
				interactive ? 'cursor-pointer hover:brightness-110 transition' : '',
				className,
			)}
		>
			<FontAwesomeIcon
				icon={faTag}
				className={cn('flex-shrink-0', ICON_SIZE_CLASSES[variant])}
			/>
			<span className="truncate max-w-[28ch]">{name}</span>
			{showBadge ? (
				<span className={BADGE_CLASSES[variant]}>{badge}</span>
			) : null}
		</Element>
	);
}
